import { z } from "zod";

import type { AdminNotificationFilter } from "@/api/zero/notificationsAdmin";

export const NOTIFICATIONS_TABS = ["all", "rejects"] as const;
export type NotificationsTab = (typeof NOTIFICATIONS_TABS)[number];

export const SEVERITY_FILTERS = ["info", "warning", "error"] as const;
export type SeverityFilter = (typeof SEVERITY_FILTERS)[number];

export const SCOPE_FILTERS = ["global", "org", "site"] as const;
export type ScopeFilter = (typeof SCOPE_FILTERS)[number];

export const CREATED_PRESETS = ["24h", "7d", "30d", "all"] as const;
export type CreatedPreset = (typeof CREATED_PRESETS)[number];

export const DEFAULT_CREATED_PRESET: CreatedPreset = "30d";
export const MIN_QUERY_LENGTH = 2;
export const SEARCH_DEBOUNCE_MS = 300;

const searchText = z
  .union([z.string(), z.number()])
  .transform(String)
  .optional()
  .catch(undefined);

export const notificationsSearchSchema = z.object({
  tab: z.enum(NOTIFICATIONS_TABS).optional().catch(undefined),
  q: searchText,
  producer: searchText,
  type: searchText,
  severity: z.enum(SEVERITY_FILTERS).optional().catch(undefined),
  scope: z.enum(SCOPE_FILTERS).optional().catch(undefined),
  scopeId: searchText,
  created: z.enum(CREATED_PRESETS).optional().catch(undefined),
  page: z.number().int().positive().optional().catch(undefined),
});

export type NotificationsSearch = z.infer<typeof notificationsSearchSchema>;

export type NotificationsFilterSearch = Omit<
  NotificationsSearch,
  "tab" | "page"
>;

export const SEVERITY_LABELS: Record<SeverityFilter, string> = {
  info: "Info",
  warning: "Warning",
  error: "Error",
};

export const SCOPE_LABELS: Record<ScopeFilter, string> = {
  global: "Global",
  org: "Org",
  site: "Site",
};

export const CREATED_LABELS: Record<CreatedPreset, string> = {
  "24h": "Last 24 hours",
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  all: "All time",
};

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

const CREATED_WINDOW_MS: Record<Exclude<CreatedPreset, "all">, number> = {
  "24h": DAY_MS,
  "7d": 7 * DAY_MS,
  "30d": 30 * DAY_MS,
};

const SEVERITY_TO_WIRE: Record<
  SeverityFilter,
  NonNullable<AdminNotificationFilter["severity"]>
> = {
  info: "SEVERITY_INFO",
  warning: "SEVERITY_WARNING",
  error: "SEVERITY_ERROR",
};

const SCOPE_TO_WIRE: Record<
  ScopeFilter,
  NonNullable<AdminNotificationFilter["scopeKind"]>
> = {
  global: "SCOPE_KIND_GLOBAL",
  org: "SCOPE_KIND_ORG",
  site: "SCOPE_KIND_SITE",
};

export function effectiveQuery(value: string | undefined): string | undefined {
  const trimmed = value?.trim() ?? "";
  return trimmed.length >= MIN_QUERY_LENGTH ? trimmed : undefined;
}

export function createdAfterFor(
  preset: CreatedPreset,
  now: number,
): string | undefined {
  if (preset === "all") return undefined;
  return new Date(now - CREATED_WINDOW_MS[preset]).toISOString();
}

export function toAdminFilter(
  search: NotificationsFilterSearch,
  now: number,
): AdminNotificationFilter {
  const scopeId =
    search.scope && search.scope !== "global"
      ? search.scopeId || undefined
      : undefined;
  return {
    query: effectiveQuery(search.q),
    producer: search.producer || undefined,
    type: search.type || undefined,
    severity: search.severity ? SEVERITY_TO_WIRE[search.severity] : undefined,
    scopeKind: search.scope ? SCOPE_TO_WIRE[search.scope] : undefined,
    scopeId,
    createdAfter: createdAfterFor(
      search.created ?? DEFAULT_CREATED_PRESET,
      now,
    ),
  };
}

export function isNarrowingFilter(filter: AdminNotificationFilter): boolean {
  return Boolean(
    filter.query ||
    filter.producer ||
    filter.type ||
    filter.severity ||
    filter.scopeKind,
  );
}

interface DescribeFilterOptions {
  scopeLabel?: string;
}

export function describeFilter(
  search: NotificationsFilterSearch,
  { scopeLabel }: DescribeFilterOptions = {},
): string[] {
  const parts: string[] = [];
  const query = effectiveQuery(search.q);
  if (query) parts.push(`“${query}”`);
  if (search.producer) parts.push(`producer ${search.producer}`);
  if (search.type) parts.push(`type ${search.type}`);
  if (search.severity)
    parts.push(`severity ${SEVERITY_LABELS[search.severity].toLowerCase()}`);
  if (search.scope === "global") parts.push("scope Global");
  if (search.scope === "org")
    parts.push(search.scopeId ? `org ${search.scopeId}` : "any org");
  if (search.scope === "site")
    parts.push(
      search.scopeId ? `site ${scopeLabel || search.scopeId}` : "any site",
    );
  parts.push(
    CREATED_LABELS[search.created ?? DEFAULT_CREATED_PRESET].toLowerCase(),
  );
  return parts;
}

export function isOneOf<T extends string>(
  options: readonly T[],
  value: unknown,
): value is T {
  return options.some((option) => option === value);
}
